import { apiClient } from './client';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

export interface MonthlyClearingRow extends Record<string, string | number> {}

export interface MonthlyAdjustment {
  station_name: string;
  clearing_month: string;
  adjust_month: string;
  adjust_item: string;
  pre_adjust_energy: number | null;
  pre_adjust_fee: number | null;
  post_adjust_energy: number | null;
  reason: string;
}

export interface MonthlyClearingData {
  rows: MonthlyClearingRow[];
  adjustments: MonthlyAdjustment[];
  available_months: string[];
  available_stations: string[];
}

export interface MonthlyClearingResponse {
  code: string;
  message: string;
  data: MonthlyClearingData;
}

export interface UploadResponse {
  code: string;
  message: string;
  data: {
    success: boolean;
    station: string;
    date: string;
    row_count: number;
  };
}

// ── Query ──
export function useMonthlyClearing(params: {
  station_name?: string;
  months?: string[];
}) {
  return useQuery<MonthlyClearingResponse, AxiosError>({
    queryKey: ['monthly-clearing', 'data', params],
    queryFn: () =>
      apiClient.get('/monthly-clearing/data', {
        params,
        paramsSerializer: { indexes: null },
      }).then((r: AxiosResponse<MonthlyClearingResponse>) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// ── Upload mutation ──
export function useMonthlyClearingUpload() {
  return useMutation<UploadResponse, AxiosError, { file: File }>({
    mutationFn: ({ file }) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiClient.post('/monthly-clearing/upload', fd).then((r: AxiosResponse<UploadResponse>) => r.data);
    },
  });
}
